import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Bell, MapPin, Globe2, Cloud, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t, lang } = useT();
  const [meta, setMeta] = useState(null);
  const [name, setName] = useState(user?.name || "");
  const [country, setCountry] = useState(user?.country_code || "");
  const [shareLoc, setShareLoc] = useState(user?.share_location !== false);
  const [reminder, setReminder] = useState({ enabled: true, time: "21:00" });

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
    api.get("/reminders").then((r) => setReminder(r.data));
  }, []);

  const saveProfile = async () => {
    const res = await api.put("/profile", { name, country_code: country || null, share_location: shareLoc });
    setUser(res.data);
    toast.success(t("profile.saved"));
  };

  const bindGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const token = localStorage.getItem("session_token");
    if (token) localStorage.setItem("link_guest_token", token);
    const redirectUrl = window.location.origin + "/chat";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const saveReminder = async () => {
    await api.put("/reminders", reminder);
    toast.success(t("profile.rem_saved"));
  };

  const enablePush = async () => {
    if (!("Notification" in window)) return toast.error(t("profile.push_no"));
    const perm = await Notification.requestPermission();
    if (perm === "granted") {
      new Notification(t("app.name"), { body: t("profile.push_body") });
      toast.success(t("profile.push_ok"));
    } else {
      toast.error(t("profile.push_deny"));
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-4">
        {user?.picture ? (
          <img src={user.picture} alt="" className="h-16 w-16 rounded-full border-2 border-[#D4AF37]/60" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#D4AF37]/60 bg-[#0A0F1D] text-2xl">
            {user?.name?.[0]}
          </div>
        )}
        <div>
          <h1 className="font-serif text-3xl font-semibold gold-text">{user?.name}</h1>
          <p className="text-sm text-[#A3B1C6]">{user?.is_guest ? t("profile.guest") : user?.email}</p>
        </div>
      </div>

      {user?.is_guest && (
        <div className="crystal border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/10 to-[#9D4CDD]/10 p-6">
          <div className="mb-2 flex items-center gap-2">
            <Cloud className="h-5 w-5 text-[#D4AF37]" />
            <h2 className="font-serif text-xl text-[#FDFBF7]">{t("profile.save_t")}</h2>
          </div>
          <p className="text-sm text-[#A3B1C6]">
            {t("profile.save_d")}
          </p>
          <Button
            onClick={bindGoogle}
            data-testid="bind-google-button"
            className="mt-4 w-full bg-gradient-to-r from-[#D4AF37] to-[#f0d787] text-[#050814] hover:opacity-90"
          >
            <Sparkles className="mr-1 h-4 w-4" /> {t("profile.bind")}
          </Button>
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-[#0A0F1D]/80 p-6">
        <div className="mb-4 flex items-center gap-2">
          <Globe2 className="h-5 w-5 text-[#D4AF37]" />
          <h2 className="font-serif text-xl text-[#FDFBF7]">{t("profile.region_privacy")}</h2>
        </div>
        <label className="text-xs tracking-[0.2em] text-[#A3B1C6]/70">{t("profile.name_label")}</label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("profile.name_ph")}
          data-testid="profile-name-input"
          className="mb-4 mt-2 border-white/10 bg-[#050814]"
        />
        <label className="text-xs tracking-[0.2em] text-[#A3B1C6]/70">{t("profile.country_label")}</label>
        <Select value={country} onValueChange={setCountry}>
          <SelectTrigger data-testid="profile-country-select" className="mt-2 border-white/10 bg-[#050814]">
            <SelectValue placeholder={t("profile.country_ph")} />
          </SelectTrigger>
          <SelectContent className="max-h-64 border-[#D4AF37]/20 bg-[#0A0F1D]">
            {meta?.countries?.map((c) => (
              <SelectItem key={c.code} value={c.code}>{lang === "en" ? c.name_en : c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="mt-4 flex items-center justify-between rounded-xl border border-white/10 bg-black/20 p-3">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[#D4AF37]" />
            <div>
              <p className="text-sm text-[#FDFBF7]">{t("profile.show_t")}</p>
              <p className="text-xs text-[#A3B1C6]">{t("profile.show_d")}</p>
            </div>
          </div>
          <Switch checked={shareLoc} onCheckedChange={setShareLoc} data-testid="share-location-switch" />
        </div>

        <Button onClick={saveProfile} data-testid="save-profile-button" className="mt-4 w-full rounded-full bg-[#D4AF37] text-[#050814] hover:opacity-90">
          {t("profile.save")}
        </Button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#0A0F1D]/80 p-6">
        <div className="mb-4 flex items-center gap-2">
          <Bell className="h-5 w-5 text-[#9D4CDD]" />
          <h2 className="font-serif text-xl text-[#FDFBF7]">{t("profile.rem_title")}</h2>
        </div>
        <div className="flex items-center justify-between rounded-xl border border-white/10 bg-black/20 p-3">
          <div>
            <p className="text-sm text-[#FDFBF7]">{t("profile.rem_enable_t")}</p>
            <p className="text-xs text-[#A3B1C6]">{t("profile.rem_enable_d")}</p>
          </div>
          <Switch
            checked={reminder.enabled}
            onCheckedChange={(v) => setReminder((p) => ({ ...p, enabled: v }))}
            data-testid="reminder-switch"
          />
        </div>
        <div className="mt-4 flex items-center gap-3">
          <label className="text-sm text-[#A3B1C6]">{t("profile.rem_time")}</label>
          <Input
            type="time"
            value={reminder.time}
            onChange={(e) => setReminder((p) => ({ ...p, time: e.target.value }))}
            data-testid="reminder-time"
            className="w-40 border-white/10 bg-[#050814]"
          />
        </div>
        <div className="mt-4 flex gap-3">
          <Button onClick={saveReminder} data-testid="save-reminder-button" className="flex-1 rounded-full bg-[#9D4CDD] hover:opacity-90">
            {t("profile.save_rem")}
          </Button>
          <Button onClick={enablePush} data-testid="enable-push-button" variant="outline" className="flex-1 rounded-full border-[#9D4CDD]/40 text-[#9D4CDD] hover:bg-[#9D4CDD]/10">
            {t("profile.enable_push")}
          </Button>
        </div>
      </div>
    </div>
  );
}
